import {
  getNamedLocalStorage,
  setNamedLocalStorage
} from '../../../utils/namedLocalStorage';

/**
 * Settings for the printer attached to this till. Stored per tenant and shop,
 * like the selected payment device, so a till that is moved between shops
 * does not print the old shop's header.
 */

const SERIAL_KEY = 'localPrinter.serial';
const LAYOUT_KEY = 'localPrinter.layout';
const HEADER_KEY = 'localPrinter.header';
const PORT_KEY = 'localPrinter.port';

export type SerialSettings = {
  baudRate: number;
  dataBits: number;
  stopBits: number;
  parity: 'none' | 'even' | 'odd';
};

export type LayoutSettings = {
  // ESC t n - printer character table. The connector ships 2.
  printerCodePage: number;
  // Characters per line at normal width on an 80mm printer.
  columns: number;
};

/**
 * Receipt header typed in on the till. A blank field falls back to the shop
 * record, so only what the shop record lacks (usually the name) needs typing.
 */
export type HeaderSettings = {
  shopName: string;
  addressLines: string[];
  vatNumber: string;
  footer: string;
};

/**
 * Which granted port is the printer, and whether to reopen it on startup.
 * The browser remembers the grant but not which of several granted ports was
 * meant, so the USB ids are kept to pick the same one again.
 */
export type PortSettings = {
  usbVendorId?: number;
  usbProductId?: number;
  autoConnect: boolean;
};

export const PORT_DEFAULTS: PortSettings = {
  autoConnect: true
};

// 9600 8N1 matches the connector for the ODP333 on macOS and for Oxhoo units.
export const SERIAL_DEFAULTS: SerialSettings = {
  baudRate: 9600,
  dataBits: 8,
  stopBits: 1,
  parity: 'none'
};

export const LAYOUT_DEFAULTS: LayoutSettings = {
  printerCodePage: 2,
  columns: 42
};

export const HEADER_DEFAULTS: HeaderSettings = {
  shopName: '',
  addressLines: [],
  vatNumber: '',
  footer: ''
};

const read = <T>(key: string, defaults: T): T => {
  try {
    const raw = getNamedLocalStorage(key);
    return { ...defaults, ...(raw ? JSON.parse(raw) : {}) };
  } catch (e) {
    return { ...defaults };
  }
};

const write = <T>(key: string, defaults: T, value: Partial<T>) => {
  setNamedLocalStorage(key, JSON.stringify({ ...defaults, ...value }));
};

export const getSerialSettings = () => read(SERIAL_KEY, SERIAL_DEFAULTS);
export const saveSerialSettings = (value: Partial<SerialSettings>) =>
  write(SERIAL_KEY, SERIAL_DEFAULTS, { ...getSerialSettings(), ...value });

export const getLayoutSettings = () => read(LAYOUT_KEY, LAYOUT_DEFAULTS);
export const saveLayoutSettings = (value: Partial<LayoutSettings>) =>
  write(LAYOUT_KEY, LAYOUT_DEFAULTS, { ...getLayoutSettings(), ...value });

export const getHeaderSettings = () => read(HEADER_KEY, HEADER_DEFAULTS);
export const saveHeaderSettings = (value: Partial<HeaderSettings>) =>
  write(HEADER_KEY, HEADER_DEFAULTS, { ...getHeaderSettings(), ...value });

export const getPortSettings = () => read(PORT_KEY, PORT_DEFAULTS);
export const savePortSettings = (value: PortSettings) =>
  write(PORT_KEY, PORT_DEFAULTS, value);
